/**
 * Section « Historique » : les pièces déposées (avec leur lien Drive), déjà
 * classées, écartées (motif) ou retirées, et à part celles que Jev a écartées
 * comme « pas une facture », à relire (décision 48). Une pièce écartée ou
 * retirée peut revenir à valider ; une pièce déposée par erreur se signale « retirée »
 * (le fichier du Drive n'est jamais touché : il se corrige à la main).
 */

import { dateFr, eurosCentimes } from '@scc/core';
import { h, vider } from '../../dom.js';
import { piecesParEtat, reprendrePiece, retirerPiece, type EtatPiece, type PieceListee } from './api.js';
import { brut, creerSection, lienSur, messageDe, table, verrouille, type Contexte, type Section } from './communs.js';
import { ETATS_PIECE, NATURES } from './pieces-textes.js';

interface FiltreHistorique {
  readonly etat: EtatPiece;
  readonly origine?: string;
  readonly libelle: string;
}

const DEPOSEES: FiltreHistorique = { etat: 'deposee', libelle: ETATS_PIECE.deposee };

const FILTRES: readonly FiltreHistorique[] = [
  DEPOSEES,
  { etat: 'deja_classee', libelle: ETATS_PIECE.deja_classee },
  { etat: 'ecartee', libelle: ETATS_PIECE.ecartee },
  { etat: 'ecartee', origine: 'jev', libelle: 'Écartée par Jev, à relire' },
  { etat: 'retiree', libelle: ETATS_PIECE.retiree },
];
const PAS = 50;

/** Un montant avec sa devise : en euros à la française, sinon le code à la suite du nombre. */
function montantFr(montant: number, devise: string | null): string {
  if (devise === null || devise === 'EUR') return eurosCentimes(montant);
  return `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(montant)} ${devise}`;
}

export interface SectionHistorique {
  readonly section: Section;
  charger(): Promise<void>;
}

export function sectionHistorique(ctx: Contexte): SectionHistorique {
  const section = creerSection(7, 'Historique', 'Ce qui a été déposé, déjà classé, écarté ou retiré');
  const filtre = h('select', { 'aria-label': 'État des pièces' }, ...FILTRES.map((f, i) => h('option', { value: String(i) }, f.libelle)));
  const retour = ctx.retour;
  const zone = h('div', {});
  let generation = 0;

  const ligne = (p: PieceListee): HTMLElement => {
    const c = p.champs;
    const montant = c?.montant.valeur ?? null;
    const fournisseur = c?.fournisseur.valeur ?? null;
    const numero = c?.numero.valeur ?? null;
    const date = c?.date.valeur ?? null;

    const suite: Node[] = [];
    if (p.etat === 'deposee' || p.etat === 'deja_classee') {
      suite.push(p.driveLien === null ? brut('Lien du Drive indisponible') : lienSur(p.driveLien, 'Ouvrir dans le Drive ↗', 'Ouvrir le fichier dans le Drive'));
      if (p.etat === 'deja_classee') suite.push(h('div', { classe: 'note-lecture' }, 'Ce fichier était déjà dans le Drive.'));
    } else {
      suite.push(brut(p.motif ?? 'Sans motif'));
      if (p.nature !== 'douteuse') suite.push(h('div', { classe: 'note-lecture' }, NATURES[p.nature]));
    }

    const bouton = (libelle: string, surClick: (b: HTMLButtonElement) => void): HTMLButtonElement => {
      const b: HTMLButtonElement = h('button', { type: 'button', classe: 'btn-action', surClick: () => { surClick(b); } }, libelle);
      return b;
    };
    const reprendre = bouton('Reprendre', (b) => {
      void (async () => {
        try {
          retour.effacer();
          await verrouille([b], () => reprendrePiece(p.id));
          retour.ok('Pièce reprise : elle est de nouveau à valider.');
          ctx.recharger('valider', 'zoho', 'historique');
        } catch (e) {
          retour.ko(messageDe(e, 'La pièce n’a pas pu être reprise.'));
        }
      })();
    });

    // Retrait d'un dépôt par erreur : un motif facultatif, une confirmation.
    const motif = h('input', { type: 'text', maxlength: 300, classe: 'champ-motif', placeholder: 'Motif, facultatif' });
    const confirmer = bouton('Confirmer le retrait', (b) => {
      void (async () => {
        try {
          retour.effacer();
          const m = motif.value.trim();
          await verrouille([b, motif], () => retirerPiece(p.id, m === '' ? undefined : m));
          retour.ok('Pièce retirée. Le fichier du Drive n’a pas été touché : corrigez-le à la main si besoin.');
          ctx.recharger('historique');
        } catch (e) {
          retour.ko(messageDe(e, 'La pièce n’a pas pu être retirée.'));
        }
      })();
    });
    const annuler = bouton('Annuler', () => {
      formRetrait.hidden = true;
    });
    const formRetrait = h(
      'div',
      { classe: 'ecart-piece', hidden: true },
      h('div', { classe: 'note-lecture' }, 'À faire quand le fichier déposé est faux : la pièce est marquée « retirée », le Drive reste tel quel.'),
      motif,
      confirmer,
      annuler,
    );
    const retirer = bouton('Retirer…', () => {
      formRetrait.hidden = false;
      motif.focus();
    });

    return h(
      'tr',
      {},
      h(
        'td',
        { classe: 'operation' },
        h('strong', {}, brut(p.etat === 'deposee' ? (p.nomPropose ?? p.nomOrigine) : p.nomOrigine)),
        p.provenance === null ? h('div', { classe: 'note-lecture' }, 'Fichier déposé à la main') : h('div', { classe: 'note-lecture' }, brut(`${p.provenance.expediteur} · ${dateFr(p.provenance.date.slice(0, 10))}`)),
      ),
      h(
        'td',
        { classe: 'operation' },
        c === null ? '—' : brut([fournisseur, numero === null ? null : `n° ${numero}`, date === null ? null : dateFr(date), montant === null ? null : montantFr(montant, c.devise.valeur)].filter((x) => x !== null).join(' · ') || '—'),
      ),
      h('td', { classe: 'operation' }, ...suite),
      h('td', {}, brut(p.traitePar ?? 'Traitée par la campagne'), h('div', { classe: 'note-lecture' }, dateFr(p.modifieLe.slice(0, 10)))),
      h(
        'td',
        {},
        h(
          'div',
          { classe: 'actions-arbitrage' },
          p.etat === 'ecartee' || p.etat === 'retiree' ? reprendre : null,
          p.etat === 'deposee' && p.driveId !== null ? retirer : null,
        ),
        formRetrait,
      ),
    );
  };

  const charger = async (): Promise<void> => {
    const moi = ++generation;
    const choisi = FILTRES[Number(filtre.value)] ?? DEPOSEES;
    const { etat } = choisi;
    const { pieces } = await piecesParEtat(etat, choisi.origine);
    if (moi !== generation) return;
    vider(zone);
    section.sousTitre(null);
    if (pieces.length === 0) {
      zone.appendChild(h('p', { classe: 'note-lecture' }, `Aucune pièce « ${choisi.libelle.toLowerCase()} ».`));
      return;
    }
    zone.appendChild(
      h(
        'p',
        { classe: 'note-lecture' },
        `${String(pieces.length)} pièce${pieces.length > 1 ? 's' : ''}, les plus récentes d’abord.`,
        choisi.origine === 'jev' ? ' Jev les juge « pas une facture » : reprenez celles qui en sont une.' : '',
      ),
    );
    const corps = table('Historique des pièces', ['Pièce', 'Facture', etat === 'deposee' || etat === 'deja_classee' ? 'Drive' : 'Motif', 'Traitement', 'Actions'], []);
    const tbody = corps.querySelector('tbody');
    let rendu = 0;
    const suite = h('button', { type: 'button', classe: 'btn-action' });
    const pied = h('div', { classe: 'barre-actions' }, suite);
    const montrer = (): void => {
      for (const p of pieces.slice(rendu, rendu + PAS)) tbody?.appendChild(ligne(p));
      rendu = Math.min(pieces.length, rendu + PAS);
      pied.hidden = rendu >= pieces.length;
      suite.textContent = `Afficher les ${String(Math.min(PAS, pieces.length - rendu))} suivantes (${String(pieces.length - rendu)} de plus)`;
    };
    suite.addEventListener('click', montrer);
    montrer();
    zone.append(corps, pied);
  };

  filtre.addEventListener('change', () => {
    void charger().catch((e: unknown) => {
      retour.ko(messageDe(e, 'L’historique n’a pas pu être chargé.'));
    });
  });

  section.corps.append(h('div', { classe: 'barre-actions' }, h('label', {}, 'Afficher ', filtre)), zone);
  return { section, charger };
}
